import { useState, useEffect } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { 
  ArrowLeft, 
  Search, 
  BookOpen,
  Rocket,
  Trophy,
  Calendar,
  Users,
  Shield,
  Heart,
  GraduationCap,
  Settings,
  HelpCircle,
  ChevronRight,
  Eye
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL || "http://localhost:8001";

// Map category icons
const categoryIcons = {
  "rocket": Rocket,
  "trophy": Trophy,
  "calendar": Calendar,
  "users": Users,
  "shield": Shield,
  "heart": Heart,
  "graduation-cap": GraduationCap,
  "settings": Settings,
  "help-circle": HelpCircle
};

export default function HelpCenter({ user }) {
  const navigate = useNavigate();
  const { articleSlug } = useParams();
  const [searchParams] = useSearchParams();
  const categoryFilter = searchParams.get("category");
  
  const [categories, setCategories] = useState([]);
  const [articles, setArticles] = useState([]);
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCategories();
    if (articleSlug) {
      fetchArticle(articleSlug);
    } else {
      fetchArticles(categoryFilter, searchQuery);
    }
  }, [articleSlug, categoryFilter]);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/api/help/categories`);
      setCategories(response.data.categories || []);
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  };

  const fetchArticles = async (category = null, search = null) => {
    try {
      setLoading(true);
      let url = `${API}/api/help/articles`;
      const params = new URLSearchParams();
      if (category) params.append("category_id", category);
      if (search) params.append("search", search);
      if (params.toString()) url += `?${params.toString()}`;
      
      const response = await axios.get(url);
      setArticles(response.data.articles || []);
      setSelectedArticle(null);
    } catch (error) {
      console.error("Error fetching articles:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchArticle = async (slug) => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/api/help/article/slug/${slug}`);
      setSelectedArticle(response.data);
    } catch (error) {
      console.error("Error fetching article:", error);
      navigate("/help");
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      fetchArticles(null, searchQuery);
      navigate(`/help?search=${encodeURIComponent(searchQuery)}`);
    } else {
      fetchArticles();
      navigate("/help");
    }
  };

  const getCategoryName = (categoryId) => {
    const cat = categories.find(c => c.id === categoryId);
    return cat?.name || categoryId;
  };

  const renderMarkdown = (content) => {
    // Simple markdown rendering
    if (!content) return null;
    
    return content.split('\n').map((line, i) => {
      // Headers
      if (line.startsWith('# ')) {
        return <h1 key={i} className="text-2xl font-bold text-white mt-6 mb-4">{line.substring(2)}</h1>;
      }
      if (line.startsWith('## ')) {
        return <h2 key={i} className="text-xl font-bold text-white mt-5 mb-3">{line.substring(3)}</h2>;
      }
      if (line.startsWith('### ')) {
        return <h3 key={i} className="text-lg font-bold text-white mt-4 mb-2">{line.substring(4)}</h3>;
      }
      
      // Lists
      if (line.startsWith('- ')) {
        return <li key={i} className="text-slate-300 ml-4 mb-1">{line.substring(2)}</li>;
      }
      if (/^\d+\. /.test(line)) {
        return <li key={i} className="text-slate-300 ml-4 mb-1 list-decimal">{line.replace(/^\d+\. /, '')}</li>;
      }
      
      // Bold text
      const boldProcessed = line.replace(/\*\*(.*?)\*\*/g, '<strong class="text-white">$1</strong>');
      
      // Empty lines
      if (line.trim() === '') {
        return <br key={i} />;
      }
      
      return <p key={i} className="text-slate-300 mb-2" dangerouslySetInnerHTML={{ __html: boldProcessed }} />;
    });
  };

  // Show single article
  if (selectedArticle) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-3xl mx-auto">
          {/* Breadcrumb */}
          <div className="flex items-center gap-2 mb-6 text-sm">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate("/help")}
              className="text-slate-400 hover:text-white"
            >
              <ArrowLeft className="w-4 h-4 mr-1" />
              Help Center
            </Button>
            <ChevronRight className="w-4 h-4 text-slate-500" />
            <span className="text-slate-400">{getCategoryName(selectedArticle.category_id)}</span>
          </div>

          {/* Article */}
          <Card className="p-8">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-xs bg-yellow-500/20 text-yellow-400 px-2 py-1 rounded">
                {getCategoryName(selectedArticle.category_id)}
              </span>
              {selectedArticle.views > 0 && (
                <span className="text-xs text-slate-500 flex items-center">
                  <Eye className="w-3 h-3 mr-1" />
                  {selectedArticle.views} views
                </span>
              )}
            </div>
            
            <div className="prose prose-invert max-w-none">
              {renderMarkdown(selectedArticle.content)}
            </div>
            
            {selectedArticle.tags && selectedArticle.tags.length > 0 && (
              <div className="mt-8 pt-6 border-t border-slate-700">
                <p className="text-xs text-slate-500 mb-2">Related topics:</p>
                <div className="flex flex-wrap gap-2">
                  {selectedArticle.tags.map((tag, i) => (
                    <span
                      key={i}
                      className="text-xs bg-slate-700 text-slate-300 px-2 py-1 rounded cursor-pointer hover:bg-slate-600"
                      onClick={() => {
                        setSearchQuery(tag);
                        fetchArticles(null, tag);
                        navigate(`/help?search=${encodeURIComponent(tag)}`);
                      }}
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </Card>
        </div>
      </div>
    );
  }

  // Show category/article list
  return (
    <div className="container mx-auto px-4 py-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-2 mb-6">
          <Button
            variant="ghost"
            onClick={() => navigate("/dashboard")}
            className="text-slate-400 hover:text-white"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Dashboard
          </Button>
        </div>

        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <div className="w-16 h-16 rounded-full bg-yellow-500/20 flex items-center justify-center">
              <BookOpen className="w-8 h-8 text-yellow-500" />
            </div>
          </div>
          <h1 className="text-3xl font-bold text-white mb-2">Help Center</h1>
          <p className="text-slate-400">Find answers and learn how to use the SCC Football Portal</p>
        </div>

        {/* Search */}
        <form onSubmit={handleSearch} className="mb-8">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
            <Input
              type="text"
              placeholder="Search help articles..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-12 py-6 text-lg bg-slate-800 border-slate-700"
            />
          </div>
        </form>

        {/* Categories Grid */}
        {!categoryFilter && !searchQuery && (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-8">
            {categories.map((category) => {
              const IconComponent = categoryIcons[category.icon] || HelpCircle;
              return (
                <Card
                  key={category.id}
                  className="p-4 cursor-pointer hover:border-yellow-500/50 transition-all"
                  onClick={() => navigate(`/help?category=${category.id}`)}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-yellow-500/20 flex items-center justify-center">
                      <IconComponent className="w-5 h-5 text-yellow-500" />
                    </div>
                    <span className="font-medium text-white">{category.name}</span>
                  </div>
                </Card>
              );
            })}
          </div>
        )}

        {/* Category Header */}
        {categoryFilter && (
          <div className="flex items-center gap-4 mb-6">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate("/help")}
              className="text-slate-400"
            >
              <ArrowLeft className="w-4 h-4 mr-1" />
              All Categories
            </Button>
            <h2 className="text-xl font-bold text-white">
              {getCategoryName(categoryFilter)}
            </h2>
          </div>
        )}

        {/* Search Results Header */}
        {searchQuery && (
          <div className="flex items-center gap-4 mb-6">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setSearchQuery("");
                fetchArticles();
                navigate("/help");
              }}
              className="text-slate-400"
            >
              <ArrowLeft className="w-4 h-4 mr-1" />
              Clear Search
            </Button>
            <h2 className="text-xl font-bold text-white">
              Search: "{searchQuery}" ({articles.length} results)
            </h2>
          </div>
        )}

        {/* Articles List */}
        {loading ? (
          <div className="text-center py-12 text-slate-400">Loading...</div>
        ) : articles.length === 0 ? (
          <Card className="p-8 text-center">
            <HelpCircle className="w-12 h-12 mx-auto mb-4 text-slate-500" />
            <h3 className="text-lg font-bold text-white mb-2">No Articles Found</h3>
            <p className="text-slate-400">
              {searchQuery 
                ? "Try a different search term or browse categories"
                : "No articles in this category yet"}
            </p>
          </Card>
        ) : (
          <div className="space-y-3">
            {articles.map((article) => (
              <Card
                key={article.id}
                className="p-4 cursor-pointer hover:border-yellow-500/30 transition-all"
                onClick={() => navigate(`/help/${article.slug}`)}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs bg-slate-700 text-slate-300 px-2 py-0.5 rounded">
                        {getCategoryName(article.category_id)}
                      </span>
                    </div>
                    <h3 className="font-bold text-white mb-1">{article.title}</h3>
                    <p className="text-sm text-slate-400 line-clamp-2">{article.summary}</p>
                  </div>
                  <ChevronRight className="w-5 h-5 text-slate-500 ml-4 flex-shrink-0" />
                </div>
              </Card>
            ))}
          </div>
        )}

        {/* Chatbot Prompt */}
        <Card className="mt-8 p-6 bg-gradient-to-br from-blue-500/10 to-purple-500/10 border-blue-500/30">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-full bg-blue-500/20 flex items-center justify-center">
              <span className="text-2xl">🐴</span>
            </div>
            <div className="flex-1">
              <h3 className="font-bold text-white">Can't find what you're looking for?</h3>
              <p className="text-sm text-slate-400">Ask Polo, our AI assistant. Click the chat icon at the bottom right!</p>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
}
